import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { PieChart } from 'react-native-chart-kit';

const { width } = Dimensions.get('window');

const NPSCalculatorScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    monthlyContribution: '5000',
    expectedReturn: '10', // p.a. during accumulation
    yearsUntilRetirement: '30',
    annuityPurchasePercent: '40', // % of corpus to buy annuity
    annuityReturnRate: '6', // p.a. from annuity
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateNPS = () => {
    const P = parseFloat(formData.monthlyContribution);
    const annualReturn = parseFloat(formData.expectedReturn);
    const years = parseFloat(formData.yearsUntilRetirement);
    const annuityPct = parseFloat(formData.annuityPurchasePercent);
    const annuityRatePA = parseFloat(formData.annuityReturnRate);

    if ([P, annualReturn, years, annuityPct, annuityRatePA].some(v => isNaN(v))) {
      setResults(null);
      return;
    }

    const r = annualReturn / 12 / 100;
    const n = years * 12;

    // FV of monthly SIP
    const corpus = P * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));

    const annuityAmount = corpus * (annuityPct / 100);
    const lumpSum = corpus - annuityAmount;
    const monthlyPension = annuityAmount * (annuityRatePA / 12 / 100);

    const investedAmount = P * n;
    const expectedReturns = corpus - investedAmount;

    setResults({
      retirementCorpus: Math.round(corpus),
      investedAmount: Math.round(investedAmount),
      expectedReturns: Math.round(expectedReturns),
      annuityPurchase: Math.round(annuityAmount),
      lumpSumWithdrawal: Math.round(lumpSum),
      estimatedMonthlyPension: Math.round(monthlyPension),
      monthlyContribution: P,
      expectedReturn: annualReturn,
      yearsUntilRetirement: years,
      annuityPurchasePercent: annuityPct,
      annuityReturnRate: annuityRatePA,
    });
  };

  useEffect(() => {
    calculateNPS();
  }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>NPS Calculator</Text>
        <Text style={styles.heroSubtitle}>
          Project your retirement corpus, lump-sum withdrawal and estimated monthly pension
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1 }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Contribution</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput
                style={styles.input}
                value={formData.monthlyContribution}
                onChangeText={(v) => handleInputChange('monthlyContribution', v)}
                keyboardType="numeric"
                placeholder="5000"
                placeholderTextColor={colors.textSecondary}
              />
            </View>
          </View>

          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Expected Return (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.expectedReturn}
                  onChangeText={(v) => handleInputChange('expectedReturn', v)}
                  keyboardType="numeric"
                  placeholder="10"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
            </View>

            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Years Until Retirement</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.yearsUntilRetirement}
                  onChangeText={(v) => handleInputChange('yearsUntilRetirement', v)}
                  keyboardType="numeric"
                  placeholder="30"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.suffixText}>Years</Text>
              </View>
            </View>
          </View>

          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Annuity Purchase (%)</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.annuityPurchasePercent}
                  onChangeText={(v) => handleInputChange('annuityPurchasePercent', v)}
                  keyboardType="numeric"
                  placeholder="40"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
              <Text style={styles.helperText}>Typical range 40% to 80% based on NPS rules</Text>
            </View>

            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Annuity Return (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.annuityReturnRate}
                  onChangeText={(v) => handleInputChange('annuityReturnRate', v)}
                  keyboardType="numeric"
                  placeholder="6"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
              <Text style={styles.helperText}>Used to estimate monthly pension payout</Text>
            </View>
          </View>
        </View>

        <View style={[styles.summaryPanel, isMobile && { flex: 1, marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Retirement Summary</Text>
            <Text style={styles.panelSubtitle}>NPS Projection Results</Text>
          </View>

          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Retirement Corpus</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.retirementCorpus)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Invested Amount</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.investedAmount)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Expected Returns</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.expectedReturns)}</Text>
              </View>
              <View style={styles.divider} />
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Lump Sum Withdrawal</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.lumpSumWithdrawal)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Annuity Purchase</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.annuityPurchase)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Est. Monthly Pension</Text>
                <Text style={styles.totalValue}>{formatCurrency(results.estimatedMonthlyPension)}</Text>
              </View>
              {renderNPSChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderNPSChart = () => {
    if (!results) return null;
    // Keep chart within summary panel; choose safe responsive widths
    const chartWidth = isMobile
      ? Math.min(screenData.width - 40, 320)
      : 320;
    const data = [
      {
        name: 'Annuity',
        population: Math.round(results.annuityPurchase),
        color: '#2563EB',
        legendFontColor: '#0f172a',
        legendFontSize: 12,
      },
      {
        name: 'Lump Sum',
        population: Math.round(results.lumpSumWithdrawal),
        color: '#10B981',
        legendFontColor: '#0f172a',
        legendFontSize: 12,
      },
    ];
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Corpus Split at Exit</Text>
        <PieChart
          data={data}
          width={chartWidth}
          height={220}
          chartConfig={{
            backgroundColor: '#ffffff',
            backgroundGradientFrom: '#ffffff',
            backgroundGradientTo: '#ffffff',
            color: (opacity=1) => `rgba(30, 64, 175, ${opacity})`,
            labelColor: (opacity=1) => `rgba(100, 116, 139, ${opacity})`,
          }}
          accessor={'population'}
          backgroundColor={'transparent'}
          paddingLeft={8}
        />
      </View>
    );
  };

  const renderNPSInformation = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>What is NPS (National Pension System)?</Text>
      <Text style={styles.sectionText}>
        NPS is a market-linked retirement scheme offering tax benefits and flexible contribution options.
        Your contributions grow until retirement and a portion of the corpus is used to buy an annuity that
        pays a monthly pension. The remainder can be withdrawn as a lump sum (subject to prevailing rules).
      </Text>

      <Text style={styles.sectionSubtitle}>Calculator methodology</Text>
      <Text style={styles.sectionText}>
        We compute your retirement corpus using the standard SIP future-value formula with monthly compounding.
        The annuity purchase percent determines the split between lump sum and annuity. Monthly pension is
        estimated as a simple monthly yield on the annuity corpus using the selected annuity rate.
        Actual pension depends on annuity product terms and market rates.
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>NPS Calculator: Complete Guide</Text>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Contribution & Investment Choices</Text>
        <Text style={styles.seoText}>Choose Active or Auto choice across Equity, Corporate bonds, and Government securities. Contributions enjoy 80C and 80CCD(1B) benefits subject to limits.</Text>
      </View>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Exit, Withdrawal & Annuity</Text>
        <Text style={styles.seoText}>At age 60, up to 60% lump sum withdrawal is permitted; at least 40% must be annuitized. Partial withdrawals are allowed under conditions after 3 years.</Text>
      </View>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Plan Better with Related Tools</Text>
        <View style={styles.linksRow}>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
            <Text style={styles.linkText}>SIP Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('RetirementCalculator')}>
            <Text style={styles.linkText}>Retirement Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SWPCalculator')}>
            <Text style={styles.linkText}>SWP Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('IncomeTaxCalculator')}>
            <Text style={styles.linkText}>Income Tax Calculator</Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.disclaimer}>Regulations and tax rules can change. Verify current NPS guidelines with PFRDA.</Text>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={24} color={colors.primary} />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What tax benefits are available?</Text>
            <Text style={styles.faqAnswer}>Deductions under Section 80CCD(1), 80CCD(1B) and 80CCD(2), subject to limits.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is annuity purchase mandatory?</Text>
            <Text style={styles.faqAnswer}>Yes, a portion of the corpus must be used to buy an annuity. The percentage depends on rules at retirement.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How accurate is the monthly pension estimate?</Text>
            <Text style={styles.faqAnswer}>It’s an approximation using the selected annuity rate; actual pension depends on annuity product and rates.</Text>
          </View>
        </View>
      )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      {renderCalculator()}
      {renderNPSInformation()}
      {renderSEOContent()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  heroSection: {
    backgroundColor: '#16a34a',
    paddingVertical: 24,
    paddingHorizontal: 30,
  },
  heroContent: {
    width: '100%',
    alignSelf: 'stretch',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
  },
  heroSubtitle: {
    fontSize: 14,
    color: '#dcfce7',
  },
  calculatorContainer: {
    width: '100%',
    alignSelf: 'stretch',
    paddingHorizontal: 30,
    paddingVertical: 20,
  },
  calculatorRow: {
    flexDirection: 'row',
    gap: 24,
  },
  formContent: {
    gap: 20,
    flex: 0.7,
  },
  summaryPanel: {
    flex: 0.3,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    height: 'fit-content',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  panelHeader: {
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  panelSubtitle: {
    fontSize: 12,
    color: '#64748b',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 16,
  },
  half: {
    flex: 1,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  helperText: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 6,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 12,
    height: 48,
  },
  currencySymbol: {
    fontSize: 16,
    color: '#64748b',
    marginRight: 8,
  },
  percentSymbol: {
    fontSize: 16,
    color: '#64748b',
    marginLeft: 8,
  },
  suffixText: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 0,
  },
  resultsContainer: {
    gap: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#334155',
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#065f46',
  },
  placeholderText: {
    fontSize: 14,
    color: '#64748b',
  },
  infoSection: {
    marginVertical: 20,
    backgroundColor: '#ffffff',
    padding: 20,
    marginHorizontal: 30,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  sectionText: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 12,
  },
  faqContainer: {
    marginHorizontal: 30,
    marginBottom: 20,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#16a34a10',
    borderRadius: 12,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  faqContent: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  faqItem: {
    marginBottom: 16,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 6,
  },
  faqAnswer: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
  },
  chartSection: {
    marginTop: 12,
    alignItems: 'center',
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  seoContainer: {
    marginHorizontal: 30,
    marginBottom: 20,
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  seoTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
    textAlign: 'center',
  },
  seoSection: {
    marginTop: 8,
    marginBottom: 16,
  },
  seoSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  seoText: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 8,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  linkButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#0ea5e910',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  linkText: {
    color: '#0ea5e9',
    fontWeight: '600',
  },
  disclaimer: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
  },
});

export default NPSCalculatorScreen;